import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { eventsAPI, quotesAPI, announcementsAPI, radioAPI } from '../../utils/api';
import { format } from 'date-fns';
import PublicLayout from '../../components/layout/PublicLayout';
import HeroCarousel from '../../components/public/HeroCarousel';
import useSEO, { PAGE_SEO } from '../../hooks/useSEO';

export default function HomePage() {
  useSEO(PAGE_SEO.home);

  const [quote, setQuote]                 = useState(null);
  const [quoteIdx, setQuoteIdx]           = useState(0);
  const [quotes, setQuotes]               = useState([]);
  const [events, setEvents]               = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [radio, setRadio]                 = useState(null);
  const [loading, setLoading]             = useState(true);

  useEffect(() => {
    Promise.all([
      quotesAPI.getAll({ featured: true }),
      // ✅ Fixed: only fetch upcoming published events for homepage
      eventsAPI.getAll({ upcoming: true, limit: 3 }),
      announcementsAPI.getAll(),
      radioAPI.getConfig(),
    ]).then(([q, e, a, r]) => {
      const qs = q.data.data || [];
      setQuotes(qs);
      setQuote(qs[0] || null);
      setEvents(e.data.data || []);
      setAnnouncements((a.data.data || []).slice(0, 5));
      setRadio(r.data.data || null);
    }).finally(() => setLoading(false));
  }, []);

  // Auto-rotate murshid quotes every 8s
  useEffect(() => {
    if (quotes.length <= 1) return;
    const t = setInterval(() => {
      setQuoteIdx(i => {
        const next = (i + 1) % quotes.length;
        setQuote(quotes[next]);
        return next;
      });
    }, 8000);
    return () => clearInterval(t);
  }, [quotes]);

  return (
    <PublicLayout>

      {/* ══ HERO ══════════════════════════════════════════════ */}
      <HeroCarousel />

      {/* ══ ANNOUNCEMENTS TICKER ══════════════════════════════ */}
      {announcements.length > 0 && (
        <div className="bg-cyan-400 text-navy-900 py-2.5 overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 flex items-center gap-4">
            <span className="flex-shrink-0 font-bold text-xs uppercase tracking-wider bg-navy-700 text-white px-2.5 py-1 rounded-full">
              News
            </span>
            <div className="overflow-hidden flex-1">
              <p className="text-sm font-medium truncate">
                📢 {announcements[0].title}
                {announcements.length > 1 && (
                  <span className="ml-4 opacity-60">+{announcements.length - 1} more</span>
                )}
              </p>
            </div>
            <Link
              to="/announcements"
              className="flex-shrink-0 text-xs font-bold underline underline-offset-2 hover:opacity-70 transition-opacity"
            >
              See all
            </Link>
          </div>
        </div>
      )}

      {/* ══ MURSHID QUOTE ROTATOR ═════════════════════════════ */}
      {quote && (
        <section className="py-16 bg-gradient-to-br from-navy-50 to-white">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <p className="text-cyan-500 text-sm font-semibold uppercase tracking-widest mb-6">
              ✦ Words of the Murshid ✦
            </p>
            <div className="bg-white rounded-3xl shadow-lg border border-navy-100 p-8 md:p-12 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 opacity-5 text-navy-900 text-9xl leading-none select-none pointer-events-none" aria-hidden>✦</div>

              {/* Title — shown when present */}
              {quote.title && (
                <p className="text-cyan-500 text-xs font-bold uppercase tracking-widest mb-4 relative z-10">
                  {quote.title}
                </p>
              )}

              {/* Arabic shown above English if available */}
              {quote.arabic_text && (
                <p className="arabic text-navy-600 text-xl md:text-2xl mb-4 leading-loose">
                  {quote.arabic_text}
                </p>
              )}

              {/* English — primary, large */}
              <p className="text-navy-800 text-xl md:text-2xl font-medium leading-relaxed mb-6">
                ❝ {quote.translation || quote.arabic_text} ❞
              </p>

              <p className="text-cyan-600 font-semibold text-sm">— {quote.murshid_name}</p>

              {/* Date line */}
              {(quote.quote_date || quote.hijri_date) && (
                <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mt-2">
                  {quote.quote_date && (
                    <span className="text-xs text-gray-400">
                      📅 {new Date(quote.quote_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </span>
                  )}
                  {quote.quote_date && quote.hijri_date && <span className="text-gray-200 text-xs">·</span>}
                  {quote.hijri_date && (
                    <span className="text-xs text-gray-400">
                      ☽ <span className="arabic" style={{ fontFamily: "'Amiri',serif" }}>{quote.hijri_date}</span>
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Dot nav */}
            {quotes.length > 1 && (
              <div className="flex justify-center gap-2 mt-5">
                {quotes.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => { setQuote(quotes[i]); setQuoteIdx(i); }}
                    aria-label={`Quote ${i + 1}`}
                    className={`h-2 rounded-full transition-all ${i === quoteIdx ? 'w-6 bg-cyan-400' : 'w-2 bg-navy-200 hover:bg-navy-300'}`}
                  />
                ))}
              </div>
            )}
            <Link to="/quotes" className="inline-block mt-6 text-navy-600 text-sm font-medium hover:text-cyan-500 transition-colors">
              View all quotes →
            </Link>
          </div>
        </section>
      )}

      {/* ══ UPCOMING EVENTS ═══════════════════════════════════ */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="section-title">Upcoming Events</h2>
              <p className="text-gray-400 text-sm mt-1">Programmes and gatherings across our branches</p>
            </div>
            <Link to="/events" className="text-cyan-500 text-sm font-semibold hover:underline">View all →</Link>
          </div>

          {events.length === 0 ? (
            <p className="text-gray-400 text-center py-12">No upcoming events at this time.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {events.map(event => (
                <Link key={event.id} to={`/events/${event.slug}`} className="card group">
                  <div className="h-44 bg-navy-100 relative overflow-hidden">
                    {event.cover_url
                      ? <img src={event.cover_url} alt={event.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                      : <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-navy-700 to-navy-900"><span className="text-5xl" aria-hidden>🌙</span></div>
                    }
                    <div className="absolute top-3 left-3 bg-cyan-400 text-navy-900 text-xs font-bold px-2.5 py-1 rounded-lg shadow">
                      {format(new Date(event.event_date), 'MMM d')}
                    </div>
                    {event.is_featured && (
                      <div className="absolute top-3 right-3 bg-white/90 text-navy-700 text-xs font-bold px-2 py-0.5 rounded-full">⭐ Featured</div>
                    )}
                  </div>
                  <div className="p-5">
                    <h3 className="font-semibold text-navy-800 mb-1 line-clamp-2 group-hover:text-cyan-600 transition-colors">{event.title}</h3>
                    {event.location && <p className="text-gray-400 text-xs">📍 {event.location}</p>}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ══ RADIO BANNER ══════════════════════════════════════ */}
      {radio && (
        <section className="py-12 bg-navy-800 relative overflow-hidden">
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none" aria-hidden>
            <div className="h-64 w-64 rounded-full bg-cyan-400/5 animate-pulse-slow" />
          </div>
          <div className="relative max-w-4xl mx-auto px-4 text-center">
            <div className="flex items-center justify-center gap-3 mb-3">
              <span className="radio-live-dot h-3 w-3 rounded-full bg-red-400 inline-block flex-shrink-0" aria-hidden />
              <h2 className="text-white text-xl font-bold">{radio.station_name}</h2>
              {radio.is_live && (
                <span className="bg-red-500/20 border border-red-400/40 text-red-300 text-xs font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">Live</span>
              )}
            </div>
            {radio.description && <p className="text-white/60 text-sm mb-6 max-w-md mx-auto">{radio.description}</p>}
            <Link to="/radio" className="btn-cyan inline-flex items-center gap-2">🎙 Listen Now</Link>
          </div>
        </section>
      )}

      {/* ══ EXPLORE TILES ═════════════════════════════════════ */}
      <section className="py-16 pattern-bg">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="section-title mb-2">Explore JNF</h2>
            <p className="text-gray-400 text-sm">Everything our JAMA has to offer</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">

            <Link to="/radio" className="sm:col-span-2 lg:col-span-1 bg-gradient-to-br from-navy-700 to-navy-900 border border-white/10 rounded-3xl p-8 flex flex-col items-center justify-center text-center hover:-translate-y-1.5 hover:shadow-2xl transition-all duration-200 group min-h-[180px]">
              <div className="text-5xl mb-4 group-hover:scale-110 transition-transform duration-200">📻</div>
              <h3 className="font-bold text-white text-lg mb-2 group-hover:text-cyan-300 transition-colors">JNF Radio</h3>
              <p className="text-white/50 text-xs leading-relaxed">Listen live — Qasidas, and Islamic programmes</p>
              <span className="mt-4 inline-flex items-center gap-1.5 bg-cyan-400/15 border border-cyan-400/30 text-cyan-300 text-xs font-bold px-3 py-1 rounded-full">
                <span className="h-1.5 w-1.5 rounded-full bg-red-400 animate-pulse inline-block" />
                Online & Local Stations
              </span>
            </Link>

            <Link to="/events" className="bg-gradient-to-br from-navy-50 to-cyan-50 hover:from-navy-100 hover:to-cyan-100 border border-white rounded-3xl p-7 flex flex-col items-center justify-center text-center hover:-translate-y-1.5 hover:shadow-lg transition-all duration-200 group min-h-[180px]">
              <div className="text-4xl mb-3 group-hover:scale-110 transition-transform duration-200">📅</div>
              <h3 className="font-bold text-navy-800 text-base mb-1.5 group-hover:text-cyan-700 transition-colors">Events & Programmes</h3>
              <p className="text-gray-500 text-xs leading-relaxed">Upcoming gatherings, Maulids, and community events</p>
            </Link>

            <Link to="/gallery" className="bg-gradient-to-br from-purple-50 to-indigo-50 hover:from-purple-100 hover:to-indigo-100 border border-white rounded-3xl p-7 flex flex-col items-center justify-center text-center hover:-translate-y-1.5 hover:shadow-lg transition-all duration-200 group min-h-[180px]">
              <div className="text-4xl mb-3 group-hover:scale-110 transition-transform duration-200">🖼</div>
              <h3 className="font-bold text-navy-800 text-base mb-1.5 group-hover:text-purple-700 transition-colors">Gallery</h3>
              <p className="text-gray-500 text-xs leading-relaxed">Photos, videos and media from JNF activities</p>
            </Link>

            <Link to="/sawia" className="bg-gradient-to-br from-amber-50 to-orange-50 hover:from-amber-100 hover:to-orange-100 border border-white rounded-3xl p-7 flex flex-col items-center justify-center text-center hover:-translate-y-1.5 hover:shadow-lg transition-all duration-200 group min-h-[180px]">
              <div className="text-4xl mb-3 group-hover:scale-110 transition-transform duration-200">🕌</div>
              <h3 className="font-bold text-navy-800 text-base mb-1.5 group-hover:text-amber-700 transition-colors">Sawia Locations</h3>
              <p className="text-gray-500 text-xs leading-relaxed">Find a Sawia near you across Nigeria and beyond</p>
            </Link>

            <Link to="/quotes" className="bg-gradient-to-br from-rose-50 to-pink-50 hover:from-rose-100 hover:to-pink-100 border border-white rounded-3xl p-7 flex flex-col items-center justify-center text-center hover:-translate-y-1.5 hover:shadow-lg transition-all duration-200 group min-h-[180px]">
              <div className="text-4xl mb-3 group-hover:scale-110 transition-transform duration-200">🌙</div>
              <h3 className="font-bold text-navy-800 text-base mb-1.5 group-hover:text-rose-700 transition-colors">Murshid Quotes</h3>
              <p className="text-gray-500 text-xs leading-relaxed">Timeless wisdom from Maolana Sayyid Musa Aloba (R.T.A)</p>
            </Link>

            <Link to="/member/register" className="bg-gradient-to-br from-cyan-400 to-cyan-600 hover:from-cyan-300 hover:to-cyan-500 border border-cyan-300/30 rounded-3xl p-7 flex flex-col items-center justify-center text-center hover:-translate-y-1.5 hover:shadow-xl transition-all duration-200 group min-h-[180px]">
              <div className="text-4xl mb-3 group-hover:scale-110 transition-transform duration-200">🤝</div>
              <h3 className="font-bold text-navy-900 text-base mb-1.5">Join JNF</h3>
              <p className="text-navy-800/70 text-xs leading-relaxed">Register as a member and access your personal dashboard</p>
            </Link>

          </div>
        </div>
      </section>

      {/* ══ JOIN CTA ═══════════════════════════════════════════ */}
      <section className="py-16 bg-gradient-to-r from-navy-700 to-navy-900 relative overflow-hidden">
        <div className="absolute inset-0 pattern-bg opacity-10" aria-hidden />
        <div className="relative max-w-3xl mx-auto px-4 text-center">
          <p className="arabic text-xl text-cyan-300 mb-4">الله حسنة</p>
          <h2 className="text-white text-2xl md:text-3xl font-bold mb-3">AHL-LAH HASSANA</h2>
          <p className="text-white/60 text-sm md:text-base mb-8 max-w-xl mx-auto leading-relaxed">
            Become a member of JNF. Access your member dashboard, connect with brothers and sisters, track your Dhikr, and stay connected with your local Sawia.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/member/register" className="btn-cyan">Register as Member</Link>
            <Link to="/member/login" className="bg-white/10 hover:bg-white/20 border border-white/20 text-white font-semibold px-6 py-3 rounded-xl text-sm transition-all">
              Member Login
            </Link>
          </div>
        </div>
      </section>

    </PublicLayout>
  );
}
